/**
 * Account chip + menu — the "who am I signed in as" surface (cloud only).
 *
 * Renders NOTHING when the host provides no account (desktop). On cloud it
 * shows an avatar chip (image, or initials fallback) that opens: name + email,
 * where the data lives, last-synced time (only when the host reports one —
 * never fabricated), Sign out, and Delete account (when the host supports it). Mounted in both TopNav
 * (desktop-width) and MobileLayout's header so identity is one tap away on every form factor.
 */
import { useState } from 'react';
import { LogOut, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@onestone/ui/dropdown-menu';
import { relativeTime } from '@/lib/time';
import { useHostAccount } from '@/lib/hostAccount';
import { DeleteAccountDialog } from './DeleteAccountDialog';

/** Initials fallback when the provider gave no avatar image. */
function initialsOf(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

export function AccountMenu() {
  const account = useHostAccount();
  const [signingOut, setSigningOut] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  if (!account) return null;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await account.signOut();
    } finally {
      // The host's auth gate flips to the login screen on session end; reset
      // covers the failure path (network error) so the item stays usable.
      setSigningOut(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Account: ${account.email}`}
            className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted/20 text-2xs font-semibold text-foreground transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {account.image ? (
              <img src={account.image} alt="" className="h-full w-full object-cover" />
            ) : (
              initialsOf(account.name, account.email)
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-semibold">{account.name || account.email}</span>
              <span className="truncate text-xs font-normal text-muted-foreground">{account.email}</span>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="px-2 py-1.5 text-xs text-muted-foreground">
            <p>Cloud workspace — your data is stored in your own private space on OneStone Cloud.</p>
            {account.lastSyncedAt ? <p className="mt-1">Last synced {relativeTime(account.lastSyncedAt)}</p> : null}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={signingOut}
            onSelect={(e) => {
              e.preventDefault();
              void handleSignOut();
            }}
            className="text-destructive focus:bg-destructive/10 focus:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </DropdownMenuItem>
          {account.deleteAccount ? (
            <DropdownMenuItem
              onSelect={() => setDeleteOpen(true)}
              className="text-destructive focus:bg-destructive/10 focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
              Delete account…
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {account.deleteAccount ? (
        <DeleteAccountDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          email={account.email}
          deleteAccount={account.deleteAccount}
          signOut={account.signOut}
        />
      ) : null}
    </>
  );
}
